import { useEffect, useState } from 'react';

interface CarouselHeroProps {
  images: string[];
  title: string;
  subtitle?: string;
  breadcrumb?: string;
  interval?: number;
}

export default function CarouselHero({
  images,
  title,
  subtitle,
  breadcrumb,
  interval = 5000,
}: CarouselHeroProps) {
  const slides = images.length ? images : [''];
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, interval);
    return () => clearInterval(timer);
  }, [slides.length, interval]);

  return (
    <section className="relative h-[420px] md:h-[500px] flex items-center overflow-hidden bg-background-950">
      {/* Background slides */}
      <div className="absolute inset-0">
        {slides.map((src, idx) => (
          <img
            key={idx}
            src={src}
            alt={`${title} — photo ${idx + 1}`}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-1000 ${
              idx === current ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8 w-full">
        {breadcrumb && (
          <p className="text-accent-400 font-label text-xs tracking-widest uppercase mb-3 flex items-center gap-2">
            <i className="ri-home-line" aria-hidden="true" />
            <span>Home</span>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span>{breadcrumb}</span>
          </p>
        )}
        <h1 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight max-w-2xl">
          {title}
        </h1>
        {subtitle && (
          <p className="text-white/80 text-base md:text-lg mt-4 max-w-xl leading-relaxed">
            {subtitle}
          </p>
        )}
        <div className="w-16 h-1 bg-accent-500 mt-6" />
      </div>

      {/* Indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-0 right-0 z-10 flex justify-center gap-2">
          {slides.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setCurrent(idx)}
              aria-label={`Show photo ${idx + 1}`}
              className={`cursor-pointer transition-all duration-300 rounded-full ${
                idx === current ? 'bg-accent-500 w-8 h-2' : 'bg-white/40 hover:bg-white/60 w-2 h-2'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}